import { RestaurantAPI } from '../services/RestaurantAPI.js';

export class LanguageSelector {
  constructor(i18n = null) {
    this.isVisible = false;
    this.currentLanguage = localStorage.getItem('tabbly-language') || 'pl';
    this.api = new RestaurantAPI();
    this.enabledLanguages = ['en', 'pl', 'de', 'es', 'fr', 'it'];
    this.i18n = i18n;
    this.allLanguages = [
      { code: 'en', name: 'English', flag: '🇺🇸' },
      { code: 'pl', name: 'Polski', flag: '🇵🇱' },
      { code: 'es', name: 'Español', flag: '🇪🇸' },
      { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
      { code: 'fr', name: 'Français', flag: '🇫🇷' },
      { code: 'it', name: 'Italiano', flag: '🇮🇹' }
    ];
  }

  async show() {
    if (this.isVisible) return;

    this.isVisible = true;

    await this.loadEnabledLanguages();

    this.render();
    this.bindEvents();

    document.body.style.overflow = 'hidden';
  }

  /**
   * Load enabled languages from the API
   */
  async loadEnabledLanguages() {
    try {
      console.log('[LanguageSelector] Loading enabled languages...');

      const restaurantId = this.getCurrentRestaurantId();
      if (!restaurantId) {
        console.warn('[LanguageSelector] No restaurant ID found, using default languages');
        return;
      }

      console.log('[LanguageSelector] Making API call for restaurant:', restaurantId);
      const translationStatus = await this.api.getTranslationStatus(restaurantId);
      console.log('[LanguageSelector] API response:', translationStatus);

      this.enabledLanguages = translationStatus.enabledLanguages || ['en'];
      console.log('[LanguageSelector] Enabled languages:', this.enabledLanguages);

      if (!this.enabledLanguages.includes(this.currentLanguage)) {
        console.log('[LanguageSelector] Current language not enabled, switching to default');
        this.currentLanguage = translationStatus.defaultLanguage || this.enabledLanguages[0] || 'en';
        localStorage.setItem('tabbly-language', this.currentLanguage);
        console.log('[LanguageSelector] New current language:', this.currentLanguage);
      }
    } catch (error) {
      console.error('[LanguageSelector] Failed to load enabled languages:', error);
    }
  }

  /**
   * Get current restaurant ID from various sources
   */
  getCurrentRestaurantId() {
    console.log('[LanguageSelector] Looking for restaurant ID...');

    if (window.app && window.app.configManager) {
      const restaurantId = window.app.configManager.getCurrentRestaurantId();
      console.log('[LanguageSelector] Found restaurant ID from ConfigurationManager:', restaurantId);
      if (restaurantId) {
        return restaurantId;
      }
    }

    const hash = window.location.hash;
    console.log('[LanguageSelector] Current hash:', hash);
    const menuMatch = hash.match(/#menu\/([^/?]+)/);
    if (menuMatch) {
      console.log('[LanguageSelector] Found restaurant ID from URL hash:', menuMatch[1]);
      return menuMatch[1];
    }

    const localStorageId = localStorage.getItem('tabbly-current-restaurant');
    const windowId = window.TABBLY_RESTAURANT_ID;
    console.log('[LanguageSelector] localStorage restaurant ID:', localStorageId);
    console.log('[LanguageSelector] window.TABBLY_RESTAURANT_ID:', windowId);

    const finalId = localStorageId || windowId || null;
    console.log('[LanguageSelector] Final restaurant ID:', finalId);
    return finalId;
  }

  /**
   * Get filtered languages based on enabled languages
   */
  getAvailableLanguages() {
    console.log('[LanguageSelector] Filtering languages...');
    console.log('[LanguageSelector] All languages:', this.allLanguages.map(l => l.code));
    console.log('[LanguageSelector] Enabled languages:', this.enabledLanguages);

    const filtered = this.allLanguages.filter(lang =>
      this.enabledLanguages.includes(lang.code)
    );

    console.log('[LanguageSelector] Available languages:', filtered.map(l => l.code));
    return filtered;
  }

  hide() {
    this.isVisible = false;

    const modalContainer = document.getElementById('language-selector-modal');
    if (modalContainer) {
      modalContainer.remove();
    }

    document.body.style.overflow = '';
  }

  t(key, fallback = key) {
    if (!this.i18n || typeof this.i18n.t !== 'function') {
      return fallback;
    }
    const translation = this.i18n.t(key);
    return translation === key ? fallback : translation;
  }

  buildTitle() {
    const english = this.i18n?.tForLanguage('language.selectTitle', 'en') || this.t('language.selectTitle', 'Select Language');
    const local = this.t('language.selectTitle', english);
    if (!local || local === english) {
      return english;
    }
    return `${english} / ${local}`;
  }

  render() {
    const modalHtml = `
      <div id="language-selector-modal" class="language-selector-modal-container">
        <div class="modal-overlay"></div>

        <div class="language-modal">
          <div class="language-modal-header">
            <h3>${this.buildTitle()}</h3>
            <button class="close-button" id="language-modal-close">
              <i class="fas fa-times"></i>
            </button>
          </div>

          <div class="language-options">
            ${this.getAvailableLanguages().map(lang => `
              <button class="language-option ${lang.code === this.currentLanguage ? 'active' : ''}"
                      data-language="${lang.code}">
                <span class="language-flag">${lang.flag}</span>
                <span class="language-name">${lang.name}</span>
                ${lang.code === this.currentLanguage ? '<i class="fas fa-check"></i>' : ''}
              </button>
            `).join('')}
          </div>
        </div>
      </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHtml);
  }

  bindEvents() {
    document.getElementById('language-modal-close')?.addEventListener('click', () => {
      this.hide();
    });

    document.querySelector('#language-selector-modal .modal-overlay')?.addEventListener('click', () => {
      this.hide();
    });

    document.querySelectorAll('.language-option').forEach(option => {
      option.addEventListener('click', (e) => {
        const languageCode = e.currentTarget.getAttribute('data-language');
        this.selectLanguage(languageCode);
      });
    });

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        this.hide();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
  }

  selectLanguage(languageCode) {
    this.currentLanguage = languageCode;
    localStorage.setItem('tabbly-language', languageCode);

    if (this.i18n && typeof this.i18n.setLanguage === 'function') {
      this.i18n.setLanguage(languageCode);
    } else {
      const event = new CustomEvent('languageChanged', {
        detail: { language: languageCode }
      });
      document.dispatchEvent(event);
    }

    this.hide();
  }

  setCurrentLanguage(languageCode) {
    this.currentLanguage = languageCode;
  }

  getCurrentLanguage() {
    return this.currentLanguage;
  }
}
